import { useCallback, useState, type FormEvent } from "react";
import { ApiError, login } from "../lib/api";
import { Button } from "./ui/Button";

interface LoginScreenProps {
  onSuccess: () => void;
}

export function LoginScreen({ onSuccess }: LoginScreenProps) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [wrongPasswordError, setWrongPasswordError] = useState<string | null>(null);
  const [connError, setConnError] = useState(false);

  const attemptLogin = useCallback(() => {
    if (loading || password.length === 0) return;
    setLoading(true);
    setWrongPasswordError(null);
    setConnError(false);
    login(password)
      .then(() => {
        setPassword("");
        onSuccess();
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError) {
          setWrongPasswordError(err.message);
        } else {
          setConnError(true);
        }
      })
      .finally(() => setLoading(false));
  }, [password, loading, onSuccess]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      attemptLogin();
    },
    [attemptLogin],
  );

  const hasError = Boolean(wrongPasswordError) || connError;

  return (
    <div className="login-screen">
      <p className="login-brand">اطلس</p>
      <p className="login-subtitle">فضای شخصی برای پیگیری کتاب، فیلم، مکان و زندگی</p>
      <form className="login-form" onSubmit={handleSubmit} noValidate>
        <label className="login-label" htmlFor="atlas-password">
          رمز عبور
        </label>
        <input
          id="atlas-password"
          name="password"
          type="password"
          dir="ltr"
          autoComplete="current-password"
          className="login-input"
          placeholder="••••"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          disabled={loading}
          aria-invalid={hasError || undefined}
          aria-describedby={wrongPasswordError ? "atlas-password-error" : undefined}
          autoFocus
        />
        {wrongPasswordError && (
          <p id="atlas-password-error" className="login-error" role="alert">
            {wrongPasswordError}
          </p>
        )}
        {connError && (
          <div className="login-conn-error" role="alert">
            <p className="login-conn-error__text">ارتباط با سرور برقرار نشد.</p>
            <button type="button" className="login-conn-error__retry" onClick={attemptLogin}>
              تلاش دوباره
            </button>
          </div>
        )}
        <Button type="submit" variant="primary" block disabled={loading || password.length === 0}>
          {loading ? "در حال ورود…" : "ورود"}
        </Button>
      </form>
    </div>
  );
}
